/* SMD product details: behaviour for sections/smd-product-details.liquid.
   Talks to the buy section (<smd-product>) only through its existing markup:
   radio inputs, the dialog openers, the add-to-cart form and its labels. */
(() => {
  if (customElements.get('smd-product-details')) return;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehavior = () => (reducedMotion() ? 'auto' : 'smooth');
  const storage = {
    get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  };

  class SmdProductDetails extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.buy = document.querySelector('smd-product');
      this.form = this.buy ? this.buy.querySelector('[data-smd-form]') : null;
      this.atc = this.form ? this.form.querySelector('[data-smd-atc]') : null;
      this.touched = false;

      this.setupLinks();
      this.setupBar();
      this.setupHarness();
      this.setupOpeners();
      this.setupMore();
      this.setupVideo();
      this.setupUnits();
    }

    /* Jump links ----------------------------------------------------------- */
    setupLinks() {
      const anchors = [...this.querySelectorAll('[data-smd-x-anchor]')].filter(el => el.dataset.smdXAnchor.trim() && el.id);
      const containers = this.querySelectorAll('[data-smd-x-links]');
      if (anchors.length < 2) return;
      containers.forEach(nav => {
        nav.innerHTML = '';
        anchors.forEach(el => {
          const a = document.createElement('a');
          a.href = `#${el.id}`;
          a.textContent = el.dataset.smdXAnchor;
          a.dataset.target = el.id;
          nav.appendChild(a);
        });
      });
      if (!('IntersectionObserver' in window)) return;
      const visible = new Map();
      const io = new IntersectionObserver(entries => {
        entries.forEach(e => visible.set(e.target.id, e.isIntersecting ? e.intersectionRatio : 0));
        let best = null;
        anchors.forEach(el => { if ((visible.get(el.id) || 0) > 0 && !best) best = el.id; });
        this.querySelectorAll('[data-smd-x-links] a').forEach(a => {
          if (a.dataset.target === best) a.setAttribute('aria-current', 'true');
          else a.removeAttribute('aria-current');
        });
      }, { rootMargin: '-35% 0px -55% 0px', threshold: [0, 0.01] });
      anchors.forEach(el => io.observe(el));
    }

    /* Sticky bar ----------------------------------------------------------- */
    setupBar() {
      this.bar = this.querySelector('[data-smd-x-bar]');
      if (!this.bar || !this.form) return;
      this.bar.hidden = false;
      this.cta = this.bar.querySelector('[data-smd-x-cta]');
      this.ctaLabel = this.bar.querySelector('[data-smd-x-cta-label]');
      this.priceEl = this.bar.querySelector('[data-smd-x-price]');
      this.summaryEl = this.bar.querySelector('[data-smd-x-summary]');
      this.mode = this.bar.dataset.mode || 'add';
      this.thumb = this.bar.querySelector('.smd-x-bar__thumb');
      this.loadVariantImages();

      // Scroll check rather than IntersectionObserver: a jump link can carry the
      // page straight past the buy form without the observer ever firing.
      let ticking = false;
      const update = () => {
        ticking = false;
        const past = this.form.getBoundingClientRect().bottom < 0;
        const top = this.headerBottom();
        if (top !== this.barTop) { this.barTop = top; this.bar.style.setProperty('--smd-x-top', `${top}px`); }
        if (past === this.bar.classList.contains('is-visible')) return;
        this.bar.classList.toggle('is-visible', past);
        this.bar.toggleAttribute('inert', !past);
      };
      const queue = () => { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } };
      window.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue, { passive: true });
      this.bar.setAttribute('inert', '');
      update();

      this.buy.addEventListener('change', e => {
        if (e.target.matches('[data-smd-opt], [data-smd-variant-input], input[type="radio"], select')) this.touched = true;
        this.syncHarness();
        this.syncBar();
      });
      this.buy.addEventListener('smd:variant-change', e => {
        const d = e.detail || {};
        const v = d.variant || d;
        if (v && v.id) this.variantId = String(v.id);
        this.syncBar();
      });
      const watch = [this.form.querySelector('[data-smd-total]'), this.form.querySelector('[data-smd-summary]'), this.atc].filter(Boolean);
      const mo = new MutationObserver(() => this.syncBar());
      watch.forEach(n => mo.observe(n, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-busy'] }));

      this.cta.addEventListener('click', () => this.onCta());
      this.syncBar();
    }

    /* Bottom edge of whatever sticky header/announcement bar is on screen now.
       Impact slides its header away on scroll, so a fixed CSS offset leaves a gap. */
    headerBottom() {
      let bottom = 0;
      document.querySelectorAll('.shopify-section--header, .shopify-section--announcement-bar, [id$="__header"], [id$="__announcement-bar"]').forEach(el => {
        const pos = getComputedStyle(el).position;
        if (pos !== 'sticky' && pos !== 'fixed') return;
        // Impact keeps the section sticky but slides <store-header> up and hides it.
        const inner = el.querySelector('store-header, .header, .announcement-bar') || el;
        if (getComputedStyle(inner).visibility === 'hidden') return;
        const r = inner.getBoundingClientRect();
        if (r.height > 0 && r.bottom > bottom) bottom = r.bottom;
      });
      return Math.max(0, Math.round(bottom));
    }

    wantsAdd() {
      if (!this.atc || this.atc.disabled) return false;
      if (this.mode === 'add') return true;
      if (this.mode === 'smart') return this.touched;
      return false;
    }

    syncBar() {
      if (!this.bar) return;
      const total = this.form.querySelector('[data-smd-total]');
      const summary = this.form.querySelector('[data-smd-summary]');
      if (total && this.priceEl) this.priceEl.textContent = total.textContent.trim();
      if (summary && this.summaryEl) this.summaryEl.textContent = summary.textContent.trim();
      const mainLabel = this.form.querySelector('[data-smd-atc-label]');
      const busy = this.atc && this.atc.getAttribute('aria-busy') === 'true';
      let label;
      if (busy && mainLabel) label = mainLabel.textContent.trim();
      else if (this.wantsAdd()) label = (mainLabel && mainLabel.textContent.trim() === this.form.dataset.labelAdded) ? mainLabel.textContent.trim() : this.bar.dataset.labelAdd;
      else label = this.bar.dataset.labelConfigure;
      this.ctaLabel.textContent = label;
      this.cta.disabled = !!busy;
      this.syncThumb();
    }

    /* Bar thumbnail follows the selected variant's image. Variant images come
       from the product's .js endpoint, so this works on every product without
       template changes; a variant with no image keeps the current thumbnail. */
    loadVariantImages() {
      if (!this.thumb) return;
      const m = window.location.pathname.match(/\/products\/[^/?#]+/);
      if (!m) return;
      fetch(`${m[0]}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(r => (r.ok ? r.json() : null))
        .then(p => {
          if (!p || !Array.isArray(p.variants)) return;
          this.variantImages = new Map();
          p.variants.forEach(v => {
            const img = v.featured_image && v.featured_image.src;
            if (img) this.variantImages.set(String(v.id), img);
          });
          this.syncThumb();
        })
        .catch(() => {});
    }

    currentVariantId() {
      const input = this.form && this.form.querySelector('input[name="id"], select[name="id"]');
      if (input && input.value) return String(input.value);
      const fallback = this.form && this.form.querySelector('[data-smd-variant-input]');
      if (fallback && fallback.value) return String(fallback.value);
      const q = new URLSearchParams(window.location.search).get('variant');
      return q || this.variantId || null;
    }

    syncThumb() {
      if (!this.thumb || !this.variantImages) return;
      const id = this.currentVariantId();
      const src = id && this.variantImages.get(id);
      if (!src || this.thumb.dataset.variantSrc === src) return;
      this.thumb.dataset.variantSrc = src;
      let url;
      try { url = new URL(src, window.location.href); url.searchParams.set('width', '120'); url = url.toString(); }
      catch (e) { url = src; }
      this.thumb.removeAttribute('srcset');
      this.thumb.removeAttribute('sizes');
      this.thumb.src = url;
    }

    onCta() {
      if (this.wantsAdd()) {
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.atc);
        else this.atc.click();
        return;
      }
      this.scrollToBuy();
    }

    scrollToBuy(focusEl) {
      const target = this.buy.querySelector('.smd-panel') || this.buy;
      target.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      const f = focusEl || target.querySelector('input:not([type="hidden"]), select, button');
      if (f) window.setTimeout(() => f.focus({ preventScroll: true }), reducedMotion() ? 0 : 450);
    }

    /* Harness cards -------------------------------------------------------- */
    radioFor(handle) {
      if (!this.buy) return null;
      return [...this.buy.querySelectorAll('input[type="radio"][data-smd-opt]')].find(r => r.value === handle) || null;
    }

    setupHarness() {
      this.harnessButtons = [...this.querySelectorAll('[data-smd-x-select]')];
      this.harnessButtons.forEach(btn => {
        if (!this.radioFor(btn.dataset.smdXSelect)) return;
        btn.hidden = false;
        btn.addEventListener('click', () => {
          const radio = this.radioFor(btn.dataset.smdXSelect);
          if (!radio) return;
          if (!radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.touched = true;
          this.syncHarness();
          this.syncBar();
          const step = radio.closest('.smd-step') || radio.closest('fieldset') || radio;
          step.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
          window.setTimeout(() => radio.focus({ preventScroll: true }), reducedMotion() ? 0 : 450);
        });
      });
      this.syncHarness();
    }

    syncHarness() {
      (this.harnessButtons || []).forEach(btn => {
        const radio = this.radioFor(btn.dataset.smdXSelect);
        const on = !!(radio && radio.checked);
        const card = btn.closest('.smd-x-hcard');
        if (card) card.classList.toggle('is-selected', on);
        btn.textContent = on ? btn.dataset.labelChosen : btn.dataset.labelChoose;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    /* Buttons that open buy-section tools ---------------------------------- */
    setupOpeners() {
      this.querySelectorAll('[data-smd-x-open]').forEach(btn => {
        const kind = btn.dataset.smdXOpen;
        if (!this.buy) return;
        if (kind === 'fit') {
          const opener = this.buy.querySelector('[data-smd-dialog-open$="-fit"]');
          if (!opener) return;
          btn.hidden = false;
          btn.addEventListener('click', () => opener.click());
        }
        if (kind === 'kit') {
          const kit = this.buy.querySelector('[id$="-complete-kit"]');
          if (!kit) return;
          btn.hidden = false;
          btn.addEventListener('click', () => {
            kit.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
            const first = kit.querySelector('select, button');
            if (first) window.setTimeout(() => first.focus({ preventScroll: true }), reducedMotion() ? 0 : 450);
          });
        }
      });
    }

    /* Feature list "show all" ---------------------------------------------- */
    setupMore() {
      this.querySelectorAll('[data-smd-x-more-toggle]').forEach(btn => {
        const list = document.getElementById(btn.getAttribute('aria-controls'));
        if (!list) return;
        const moreLabel = btn.textContent.trim();
        btn.addEventListener('click', () => {
          const open = btn.getAttribute('aria-expanded') !== 'true';
          list.querySelectorAll('[data-smd-x-more]').forEach(li => { li.hidden = !open; });
          btn.setAttribute('aria-expanded', String(open));
          btn.textContent = open ? btn.dataset.labelLess : moreLabel;
          if (!open) list.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
        });
      });
    }

    /* Video ---------------------------------------------------------------- */
    setupVideo() {
      this.querySelectorAll('[data-smd-x-video]').forEach(frame => {
        const clip = frame.querySelector('video');
        if (clip) {
          clip.muted = true;
          clip.setAttribute('aria-hidden', 'true');
          clip.removeAttribute('autoplay');
          clip.pause();
          if (!reducedMotion() && 'IntersectionObserver' in window) {
            new IntersectionObserver(([e]) => {
              if (e.isIntersecting) { clip.preload = 'auto'; const p = clip.play(); if (p && p.catch) p.catch(() => {}); }
              else clip.pause();
            }, { threshold: 0.25 }).observe(clip);
          }
        }
        const play = frame.querySelector('[data-smd-x-play]');
        const id = frame.dataset.youtube;
        if (!play || !id) return;
        play.addEventListener('click', () => {
          const iframe = document.createElement('iframe');
          iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
          iframe.title = frame.dataset.title || 'Product video';
          iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';
          iframe.allowFullscreen = true;
          frame.innerHTML = '';
          frame.appendChild(iframe);
          iframe.focus();
        });
      });
    }

    /* US / metric ---------------------------------------------------------- */
    setupUnits() {
      const buttons = [...this.querySelectorAll('[data-smd-x-units]')];
      if (!buttons.length) return;
      const apply = units => {
        this.dataset.units = units;
        buttons.forEach(b => b.setAttribute('aria-pressed', String(b.dataset.smdXUnits === units)));
      };
      apply(storage.get('smd-units') === 'metric' ? 'metric' : 'us');
      buttons.forEach(b => b.addEventListener('click', () => {
        apply(b.dataset.smdXUnits);
        storage.set('smd-units', b.dataset.smdXUnits);
      }));
    }
  }

  customElements.define('smd-product-details', SmdProductDetails);
})();
